export const DEFAULT_LOGIN_ENTRY = 'admin'

const MOBILE_DEFAULT_LOGIN_ENTRY = 'report'
const MOBILE_USER_AGENT_PATTERN = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i

export const LOGIN_ENTRY_CONFIG = {
  admin: {
    key: 'admin',
    loginPath: '/admin/login',
    homePath: '/admin/manageAccount',
    label: '管理员',
    avatar: '管'
  },
  report: {
    key: 'report',
    loginPath: '/report/login',
    homePath: '/report/reportCenter',
    label: '举报员',
    avatar: '举'
  },
  verify: {
    key: 'verify',
    loginPath: '/verify/login',
    homePath: '/verify/verifyCenter',
    label: '核证员',
    avatar: '核'
  }
}

const PROTECTED_PATH_ENTRY_MAP = {
  admin: [
    '/admin/manageAccount',
    '/admin/platformAccount',
    '/admin/dictionaryList',
    '/admin/clueList',
    '/admin/clueGroupList',
    '/admin/productList'
  ],
  report: [
    '/report/reportCenter',
    '/report/reportCreate',
    '/report/reportDetail'
  ],
  verify: [
    '/verify/verifyCenter',
    '/verify/verifyDetail',
    '/verify/verifyCluePool',
    '/verify/verifyClueDetail',
    '/verify/verifyNotice'
  ]
}

const getDefaultLoginEntry = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_LOGIN_ENTRY
  }

  const userAgent = window.navigator?.userAgent || ''
  const isMobileByUserAgent = MOBILE_USER_AGENT_PATTERN.test(userAgent)
  const isMobileByUserAgentData = Boolean(window.navigator?.userAgentData?.mobile)
  const maxTouchPoints = Number(window.navigator?.maxTouchPoints || 0)
  const isMobileByViewport =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(max-width: 768px)').matches &&
    maxTouchPoints > 0

  return isMobileByUserAgent || isMobileByUserAgentData || isMobileByViewport
    ? MOBILE_DEFAULT_LOGIN_ENTRY
    : DEFAULT_LOGIN_ENTRY
}

const normalizeEntry = (entry) => (LOGIN_ENTRY_CONFIG[entry] ? entry : getDefaultLoginEntry())
const getTokenStorageKey = (entry) => `token_${normalizeEntry(entry)}`
const getUserStorageKey = (entry) => `user_${normalizeEntry(entry)}`
const LOGIN_USER_ID_FIELDS = ['id', 'user_id', 'userId', 'admin_id', 'adminId', 'uid', 'sub']

const normalizeStoredValue = (value) => {
  if (value === null || value === undefined) {
    return ''
  }

  const normalizedValue = String(value).trim()
  if (!normalizedValue || normalizedValue === 'null' || normalizedValue === 'undefined') {
    return ''
  }

  return normalizedValue
}

const extractLoginUserId = (payload = {}) => {
  if (!payload || typeof payload !== 'object') {
    return ''
  }

  return LOGIN_USER_ID_FIELDS
    .map((field) => normalizeStoredValue(payload[field]))
    .find(Boolean) || ''
}

const decodeBase64Url = (value = '') => {
  if (!value) {
    return ''
  }

  const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/')
  const paddingLength = (4 - (normalizedValue.length % 4)) % 4
  const paddedValue = `${normalizedValue}${'='.repeat(paddingLength)}`

  try {
    if (typeof window !== 'undefined' && typeof window.atob === 'function') {
      return decodeURIComponent(
        Array.from(window.atob(paddedValue))
          .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
          .join('')
      )
    }
  } catch (error) {
    return ''
  }

  return ''
}

const parseTokenPayload = (token = '') => {
  const normalizedToken = normalizeStoredValue(token)
  if (!normalizedToken || !normalizedToken.includes('.')) {
    return null
  }

  const tokenSections = normalizedToken.split('.')
  if (tokenSections.length < 2) {
    return null
  }

  try {
    const decodedPayload = decodeBase64Url(tokenSections[1])
    return decodedPayload ? JSON.parse(decodedPayload) : null
  } catch (error) {
    return null
  }
}

export const getLoginEntry = () => {
  if (typeof window === 'undefined') {
    return DEFAULT_LOGIN_ENTRY
  }

  return normalizeEntry(window.localStorage.getItem('loginEntry'))
}

export const setLoginEntry = (entry) => {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.setItem('loginEntry', normalizeEntry(entry))
}

export const getLoginMetaByEntry = (entry) => LOGIN_ENTRY_CONFIG[normalizeEntry(entry)]

export const getLoginPathByEntry = (entry) => getLoginMetaByEntry(entry).loginPath

export const getHomePathByEntry = (entry) => getLoginMetaByEntry(entry).homePath

export const getTokenByEntry = (entry) => {
  if (typeof window === 'undefined') {
    return ''
  }

  return window.localStorage.getItem(getTokenStorageKey(entry)) || ''
}

export const hasTokenByEntry = (entry) => Boolean(getTokenByEntry(entry))

export const setTokenByEntry = (entry, token) => {
  if (typeof window === 'undefined') {
    return
  }

  const normalizedEntry = normalizeEntry(entry)
  window.localStorage.setItem(getTokenStorageKey(normalizedEntry), token)
  setLoginEntry(normalizedEntry)
}

export const getLoginUserByEntry = (entry) => {
  if (typeof window === 'undefined') {
    return null
  }

  const rawValue = window.localStorage.getItem(getUserStorageKey(entry))
  if (!rawValue) {
    return null
  }

  try {
    const parsedValue = JSON.parse(rawValue)
    return parsedValue && typeof parsedValue === 'object' ? parsedValue : null
  } catch (error) {
    return null
  }
}

export const getLoginUserIdByEntry = (entry) => {
  const user = getLoginUserByEntry(entry)
  const storedUserId = extractLoginUserId(user || {})
  if (storedUserId) {
    return storedUserId
  }

  return extractLoginUserId(parseTokenPayload(getTokenByEntry(entry)) || {})
}

export const setLoginUserByEntry = (entry, user) => {
  if (typeof window === 'undefined') {
    return
  }

  const normalizedEntry = normalizeEntry(entry)
  const normalizedUser = user && typeof user === 'object' ? user : {}

  window.localStorage.setItem(getUserStorageKey(normalizedEntry), JSON.stringify(normalizedUser))
  setLoginEntry(normalizedEntry)
}

export const removeLoginUserByEntry = (entry) => {
  if (typeof window === 'undefined') {
    return
  }

  window.localStorage.removeItem(getUserStorageKey(entry))
}

export const removeTokenByEntry = (entry) => {
  if (typeof window === 'undefined') {
    return
  }

  const normalizedEntry = normalizeEntry(entry)
  window.localStorage.removeItem(getTokenStorageKey(normalizedEntry))
  removeLoginUserByEntry(normalizedEntry)
}

export const getPreferredEntry = () => {
  const currentEntry = getLoginEntry()

  if (hasTokenByEntry(currentEntry)) {
    return currentEntry
  }

  const fallbackEntry = Object.keys(LOGIN_ENTRY_CONFIG).find((entry) => hasTokenByEntry(entry))
  return fallbackEntry || getDefaultLoginEntry()
}

export const getEntryByLoginPath = (path) => {
  const matched = Object.values(LOGIN_ENTRY_CONFIG).find((item) => item.loginPath === path)
  return matched ? matched.key : null
}

export const getEntryByProtectedPath = (path) => {
  const matched = Object.entries(PROTECTED_PATH_ENTRY_MAP).find(([, paths]) => paths.includes(path))
  return matched ? matched[0] : null
}

export const isLoginPath = (path) => Boolean(getEntryByLoginPath(path))
